// Application entry point: capability check (F-01.11, E-17), then wire the UI modules.

// The old address forwards to the new one before anything starts (2026-10-03).
import './redirect';
import './styles.css';
import { app } from './app/app';
import { applyI18n, onLangChange, setLang, t } from './i18n';
import { $, esc, toast } from './ui/dom';
import { initToolbar, updateToolbar } from './ui/toolbar';
import { initTimeline, updateTimeline } from './ui/timeline';
import { initInteractions } from './ui/interactions';
import { initKeyboard } from './ui/keyboard';
import { initDock, updateDock } from './ui/dock';
import { initAlignPanel, updateAlignPanel } from './ui/align-panel';
import { updateOverlays } from './ui/badges';
import { initModals } from './ui/modals';
import { kvGet, kvSet } from './app/persist';
import type { UserSettings } from './core/types';
import { initExport, updateExport } from './ui/export-dialog';
import { initTemporal, updateTemporal } from './ui/temporal-panel';
import { initQuality, updateQuality } from './ui/quality-panel';
import { initProjectTree, updateProjectTree } from './ui/project-tree';

interface Capability {
  ok: boolean;
  missing: string[];
}

function checkCapabilities(): Capability {
  const missing: string[] = [];
  if (!window.isSecureContext) missing.push('cap.secure');
  if (typeof VideoDecoder === 'undefined' || typeof VideoFrame === 'undefined') missing.push('cap.webcodecs');
  const c = document.createElement('canvas');
  if (!c.getContext('webgl2')) missing.push('cap.webgl2');
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') missing.push('cap.workers');
  if (typeof indexedDB === 'undefined') missing.push('cap.indexeddb');
  return { ok: missing.length === 0, missing };
}

async function main(): Promise<void> {
  const saved = await kvGet<UserSettings>('settings');
  if (saved) Object.assign(app.state.settings, saved);
  setLang(app.state.settings.language);
  applyI18n();

  const cap = checkCapabilities();
  if (!cap.ok) {
    const el = $('#unsupported');
    el.hidden = false;
    el.innerHTML =
      `<h1>${esc(t('cap.title'))}</h1><p>${esc(t('cap.body'))}</p><ul>` +
      cap.missing.map((k) => `<li>${esc(t(k))}</li>`).join('') +
      `</ul><p class="muted">${esc(navigator.userAgent)}</p>`;
    return;
  }
  // File System Access is optional (saving falls back to download); HEVC support is reported per file (F-01.5).

  app.init($<HTMLCanvasElement>('#view'), $<HTMLCanvasElement>('#overlay'));
  await app.loadCustomPresets();
  initToolbar(app);
  initTimeline(app);
  initInteractions(app);
  initKeyboard(app);
  initDock(app);
  initAlignPanel(app);
  initModals(app);
  initExport(app);
  initTemporal(app);
  initQuality(app);
  initProjectTree(app);

  const refresh = () => {
    updateToolbar(app);
    updateTimeline(app);
    updateDock(app);
    updateAlignPanel(app);
    updateExport(app);
    updateTemporal(app);
    updateQuality(app);
    updateProjectTree(app);
  };
  app.store.subscribe(refresh);
  // User settings live in IndexedDB (docs/04-data-model.md); persist whenever they change.
  let settingsJson = JSON.stringify(app.state.settings);
  app.store.subscribe((s) => {
    const j = JSON.stringify(s.settings);
    if (j !== settingsJson) {
      settingsJson = j;
      void kvSet('settings', s.settings);
    }
  });
  app.view.onRendered = () => {
    updateOverlays(app);
  };
  onLangChange(() => {
    applyI18n();
    refresh();
    app.view.request();
  });
  // E-18/E-20: tell the user when the GPU context is lost; the renderer rebuilds itself when it comes back.
  app.view.canvas.addEventListener('webglcontextlost', () => toast(t('error.glLost'), 6000));
  app.view.canvas.addEventListener('webglcontextrestored', () => toast(t('error.glRestored')));
  refresh();
  app.view.request();

  window.addEventListener('beforeunload', (e) => {
    void app.ws.persist();
    if (app.ws.anyUnsavedWork()) e.preventDefault();
  });
  // Reopen the projects of the last session (F-18); videos with remembered permission open by themselves.
  await app.ws.restore();

  // Development hook for automated tests (no network, local only).
  (window as unknown as { __app: typeof app }).__app = app;
}

void main();
